import { downloadCsv } from "./downloadCsv";
import { displayName, mapPeople, type MappedPersonRow, type ReportType, type Variables } from "./peopleReportMapping";

export const EXPORT_PAGE_SIZE = 100;
export const EXPORT_MAX_PAGES = 50;

type PageInfo = {
  hasNextPage?: boolean | null;
  endCursor?: string | null;
};

type PeopleConnection = {
  nodes?: unknown[] | null;
  pageInfo?: PageInfo | null;
};

type FetchPeoplePage = (options: {
  variables: {
    input: Record<string, unknown>;
    includeSuspensions: boolean;
    includeTerminations: boolean;
    first: number;
    after?: string | null;
  };
}) => Promise<{ data?: { people?: PeopleConnection | null } | null; error?: Error }>;

export const csvFilenameForExport = (reportType: ReportType, variables: Variables, date = new Date()): string => {
  const stamp = date.toISOString().slice(0, 10);
  if (reportType === "suspensions") {
    return `npdu-suspensions-${stamp}.csv`;
  }
  if (variables.reestablished === true) {
    return `npdu-reestablished-${stamp}.csv`;
  }
  if (variables.reestablished === false) {
    return `npdu-terminated-${stamp}.csv`;
  }
  return `npdu-terminations-${stamp}.csv`;
};

export const buildPeopleCsvRows = (rows: MappedPersonRow[], reportType: ReportType): string[][] => {
  if (reportType === "suspensions") {
    return [
      ["Name", "PGA ID", "Class", "Status", "Section", "Primary Facility", "Suspension Date"],
      ...rows.map((row) => [
        displayName(row),
        row.pgaId || "",
        row.classification || "",
        row.status || "",
        row.section || "",
        row.primaryFacility || "",
        row.suspensionsDate || ""
      ])
    ];
  }

  return [
    ["Name", "PGA ID", "Class", "Status", "Section", "Primary Facility", "Termination Date", "Re-established Date"],
    ...rows.map((row) => [
      displayName(row),
      row.pgaId || "",
      row.classification || "",
      row.status || "",
      row.section || "",
      row.primaryFacility || "",
      row.terminationsDate || "",
      row.reestablishedDate || ""
    ])
  ];
};

export const fetchAllPeopleForExport = async ({
  fetchPage,
  input,
  reportType
}: {
  fetchPage: FetchPeoplePage;
  input: Record<string, unknown>;
  reportType: ReportType;
}): Promise<{ nodes: unknown[]; truncated: boolean }> => {
  const allNodes: unknown[] = [];
  let after: string | null | undefined = undefined;
  let hasNextPage = true;
  let pageCount = 0;
  const includeSuspensions = reportType === "suspensions";
  const includeTerminations = reportType === "terminations";

  while (hasNextPage && pageCount < EXPORT_MAX_PAGES) {
    const { data, error } = await fetchPage({
      variables: {
        input,
        includeSuspensions,
        includeTerminations,
        first: EXPORT_PAGE_SIZE,
        after
      }
    });

    if (error) {
      throw error;
    }

    const connection = data?.people;
    const nodes = connection?.nodes || [];
    const endCursor = connection?.pageInfo?.endCursor;
    const nextPage = Boolean(connection?.pageInfo?.hasNextPage);

    // Empty page or hasNextPage without a cursor would re-fetch the same page and duplicate rows.
    if (nodes.length === 0 || (nextPage && !endCursor)) {
      if (nodes.length > 0) {
        allNodes.push(...nodes);
      }
      hasNextPage = false;
      break;
    }

    allNodes.push(...nodes);
    hasNextPage = nextPage;
    after = endCursor;
    pageCount += 1;
  }

  return { nodes: allNodes, truncated: hasNextPage };
};

export const exportPeopleCsv = ({
  nodes,
  reportType,
  variables
}: {
  nodes: unknown[];
  reportType: ReportType;
  variables: Variables;
}): number => {
  const mapped = mapPeople(nodes);
  downloadCsv(buildPeopleCsvRows(mapped, reportType), csvFilenameForExport(reportType, variables));
  return mapped.length;
};
